import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GpsLogo } from "@/components/Layout";
import api from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("admin@gpsstats.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [bgUrl, setBgUrl] = useState("https://images.unsplash.com/photo-1546519638-68e109498ffc?q=80&w=2400&auto=format&fit=crop");

  useEffect(() => {
    api.get("/site/settings").then((r) => {
      if (r.data?.login_bg_url) setBgUrl(r.data.login_bg_url);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back, operator.");
      const from = loc.state?.from?.pathname;
      // Decide landing based on role
      const me = JSON.parse(localStorage.getItem("gps_user") || "null");
      const role = me?.role;
      const fallback = role === "admin" ? "/admin" : "/admin/games";
      nav(from || fallback, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy text-white flex">
      {/* Left visual side */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden border-r border-navy-border">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${bgUrl})`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/80 to-navy-deep/95" />
        <div className="absolute inset-0 court-grid opacity-40" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <GpsLogo />
          <div className="space-y-4 max-w-md">
            <div className="inline-block bg-orange text-white text-xs font-heading uppercase tracking-[0.3em] px-3 py-1">
              Operator Console
            </div>
            <h2 className="font-heading font-black text-5xl uppercase leading-none tracking-tight">
              Run the<br />
              <span className="text-orange">3×3 game</span><br />
              like a federation.
            </h2>
            <p className="text-ink-secondary">
              Live scoring, real-time play-by-play, FIBA-style box scores and PDFs. Built for tournament operators.
            </p>
          </div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-ink-muted">
            v1.0 · auth: jwt · region: global
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex justify-center"><GpsLogo /></div>
          <div className="mb-8">
            <div className="text-orange text-xs font-heading uppercase tracking-[0.3em] mb-2">Restricted area</div>
            <h1 className="font-heading font-black text-4xl uppercase tracking-tight">Sign in</h1>
            <p className="text-ink-secondary mt-2 text-sm">Use your operator credentials. Public visitors can browse without login.</p>
          </div>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-navy-deep border-navy-border text-white mt-2 rounded-sm focus-visible:ring-orange focus-visible:ring-1 focus-visible:border-orange"
                required
              />
            </div>
            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-navy-deep border-navy-border text-white mt-2 rounded-sm focus-visible:ring-orange focus-visible:ring-1 focus-visible:border-orange"
                required
              />
            </div>
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-[0.2em] py-6 rounded-sm"
            >
              {loading ? "Authenticating…" : "Enter the console"}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/" className="text-ink-secondary hover:text-orange text-xs uppercase tracking-widest font-heading">
              ← Back to public portal
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
